import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { mergeST } from "@/lib/staffingContent";
import { computeStaffing } from "@/lib/staffingEngine";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { HardHat, ExternalLink, Loader2, RotateCcw, Plus, Trash2 } from "lucide-react";
import { Field, Sel, Panel } from "@/components/admin/PartnershipSettingsForm";

const inr = (n) => `₹${new Intl.NumberFormat("en-IN").format(Math.round(n || 0))}`;
const num = (n) => new Intl.NumberFormat("en-IN").format(Math.round(n || 0));

const FORMULA_FIELDS = [
  ["General", [
    ["sqft_per_guest", "Sq.ft per event guest (capacity estimate)"],
    ["cost_factor", "Employment cost factor (loaded salary ×)"],
    ["deployable_pct", "% of operational team deployable for events"],
  ]],
  ["Leadership (Duty Managers)", [
    ["duty1_rooms", "1st Duty Mgr — rooms >"], ["duty1_seats", "1st Duty Mgr — seats >"], ["duty1_capacity", "1st Duty Mgr — capacity >"],
    ["duty2_rooms", "2nd Duty Mgr — rooms >"], ["duty2_capacity", "2nd Duty Mgr — capacity >"],
  ]],
  ["Front office", [
    ["fo_base", "Base team"], ["fo_rooms_per", "1 per N rooms"], ["fo_event_threshold", "+1 when capacity >"],
  ]],
  ["Security", [
    ["sec_per_gate", "Staff per 24h gate"], ["sec_outdoor_threshold", "+1 guard when outdoor sqft >"],
  ]],
  ["Housekeeping", [
    ["hk_rooms_per", "Rooms per attendant"], ["hk_leave_factor", "Leave coverage factor"],
    ["hks_rooms", "Supervisor — rooms ≥"], ["hks_outdoor", "Supervisor — outdoor sqft ≥"],
    ["hks2_rooms", "2nd supervisor — rooms >"], ["hks2_capacity", "2nd supervisor — capacity >"],
    ["pa_outdoor_per", "Public area — sqft per person"], ["pa_seats_per", "Public area — seats per person"], ["pa_leave_factor", "Public area — leave factor"],
  ]],
  ["Gardening & engineering", [
    ["garden_per", "Sq.ft per gardener"], ["garden_factor", "Gardening coverage factor"],
    ["eng_min", "Engineering minimum team"], ["eng_rooms_per", "1 per N rooms"], ["eng_outdoor_per", "1 per N outdoor sqft"],
  ]],
  ["F&B and kitchen", [
    ["capt_per", "Seats per captain"], ["server_per", "Seats per server"], ["server_leave_factor", "Server leave factor"],
    ["cook_per", "Seats per cook"], ["cook_min", "Minimum cooks"], ["helper_per", "Seats per helper"], ["helper_min", "Minimum helpers"],
    ["steward_per", "Seats per steward"], ["steward_min", "Minimum stewards"],
  ]],
  ["Stores & laundry", [
    ["stores_rooms", "2nd stores person — rooms >"], ["stores_seats", "2nd stores person — seats >"], ["laundry_rooms", "Laundry coordinator — rooms ≥"],
  ]],
];

function FormulasTab({ cfg, set }) {
  const setF = (k, v) => set("formulas", { ...cfg.formulas, [k]: v });
  return (
    <>
      {FORMULA_FIELDS.map(([group, fields]) => (
        <Panel key={group} title={group}>
          <div className="grid sm:grid-cols-3 gap-4">
            {fields.map(([k, label]) => (
              <Field key={k} label={label} type="number" value={cfg.formulas[k]} onChange={(v) => setF(k, v)} testId={`sc-cms-f-${k}`} />
            ))}
          </div>
        </Panel>
      ))}
    </>
  );
}

function SalariesTab({ cfg, set }) {
  const setRole = (id, k, v) => set("roles", { ...cfg.roles, [id]: { ...cfg.roles[id], [k]: v } });
  const setCR = (i, k, v) => set("custom_roles", cfg.custom_roles.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const factor = Number(cfg.formulas.cost_factor) || 1.22;
  return (
    <>
      <Panel title={`Role salary bands — Bangalore benchmarks · loaded cost = recommended × ${factor} (factor editable in Formulas)`}>
        <p className="text-[11px] text-zinc-400 mb-3">Monthly gross cash salary. The <strong>recommended</strong> value drives all calculations; min/max are the market band for reference during hiring. Revalidate the benchmark every six months.</p>
        <div className="hidden sm:grid grid-cols-[1.6fr,90px,90px,90px,110px] gap-3 px-1 pb-1 text-[10px] uppercase tracking-wide text-zinc-400 font-medium">
          <span>Role</span><span>Min</span><span>Recommended</span><span>Max</span><span className="text-right">Loaded cost</span>
        </div>
        {Object.entries(cfg.roles).map(([id, r]) => (
          <div key={id} className="grid grid-cols-2 sm:grid-cols-[1.6fr,90px,90px,90px,110px] gap-3 items-end border-b border-zinc-100 py-2" data-testid={`sc-cms-role-${id}`}>
            <Field label="Role" value={r.label} onChange={(v) => setRole(id, "label", v)} />
            <Field label="Min" type="number" value={r.min} onChange={(v) => setRole(id, "min", v)} testId={`sc-cms-min-${id}`} />
            <Field label="Recommended" type="number" value={r.salary} onChange={(v) => setRole(id, "salary", v)} testId={`sc-cms-salary-${id}`} />
            <Field label="Max" type="number" value={r.max} onChange={(v) => setRole(id, "max", v)} testId={`sc-cms-max-${id}`} />
            <span className="text-xs text-zinc-400 pb-2.5 text-right tabular-nums">{inr((Number(r.salary) || 0) * factor)}/mo</span>
          </div>
        ))}
      </Panel>
      <Panel title="Additional custom roles — added on top of the formula-driven team" right={
        <Button variant="outline" size="sm" onClick={() => set("custom_roles", [...(cfg.custom_roles || []), { id: `cr_${Date.now()}`, label: "New role", min: 0, salary: 20000, max: 0, count: 1 }])} data-testid="sc-cms-cr-add">
          <Plus className="w-4 h-4 mr-1" /> Add role
        </Button>
      }>
        <p className="text-[11px] text-zinc-400 mb-3">Roles the formulas don&apos;t cover (e.g. naturalist, pool lifeguard, night auditor, activity host). Each appears in the plan under &quot;Additional roles&quot; with the fixed headcount you set here.</p>
        {(cfg.custom_roles || []).length === 0 && <div className="text-sm text-zinc-400 py-4 text-center">No custom roles yet.</div>}
        {(cfg.custom_roles || []).map((r, i) => (
          <div key={r.id || i} className="grid grid-cols-2 sm:grid-cols-[1.6fr,80px,90px,90px,90px,110px,36px] gap-3 items-end border-b border-zinc-100 py-2" data-testid={`sc-cms-cr-${i}`}>
            <Field label="Role" value={r.label} onChange={(v) => setCR(i, "label", v)} testId={`sc-cms-cr-label-${i}`} />
            <Field label="Headcount" type="number" value={r.count} onChange={(v) => setCR(i, "count", v)} testId={`sc-cms-cr-count-${i}`} />
            <Field label="Min" type="number" value={r.min} onChange={(v) => setCR(i, "min", v)} />
            <Field label="Recommended" type="number" value={r.salary} onChange={(v) => setCR(i, "salary", v)} testId={`sc-cms-cr-salary-${i}`} />
            <Field label="Max" type="number" value={r.max} onChange={(v) => setCR(i, "max", v)} />
            <span className="text-xs text-zinc-400 pb-2.5 text-right tabular-nums">{inr((Number(r.salary) || 0) * factor * (Number(r.count) || 0))}/mo</span>
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => set("custom_roles", cfg.custom_roles.filter((_, j) => j !== i))} data-testid={`sc-cms-cr-del-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        ))}
      </Panel>
    </>
  );
}

function EventTab({ cfg, set }) {
  const setER = (i, k, v) => set("event_roles", cfg.event_roles.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <Panel title="Event-day roles — requirement = CEILING(event guests ÷ ratio), with minimum" right={
      <Button variant="outline" size="sm" onClick={() => set("event_roles", [...cfg.event_roles, { id: `er_${Date.now()}`, label: "New role", per_guests: 100, min: 0, day_rate: 800 }])} data-testid="sc-cms-er-add">
        <Plus className="w-4 h-4 mr-1" /> Add role
      </Button>
    }>
      {cfg.event_roles.map((r, i) => (
        <div key={r.id || i} className="grid grid-cols-[1fr,110px,90px,120px,36px] gap-3 items-end mb-2" data-testid={`sc-cms-er-${i}`}>
          <Field label="Role" value={r.label} onChange={(v) => setER(i, "label", v)} testId={`sc-cms-er-label-${i}`} />
          <Field label="1 per N guests" type="number" value={r.per_guests} onChange={(v) => setER(i, "per_guests", v)} testId={`sc-cms-er-per-${i}`} />
          <Field label="Minimum" type="number" value={r.min} onChange={(v) => setER(i, "min", v)} />
          <Field label="Day rate (₹)" type="number" value={r.day_rate} onChange={(v) => setER(i, "day_rate", v)} testId={`sc-cms-er-rate-${i}`} />
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => set("event_roles", cfg.event_roles.filter((_, j) => j !== i))}><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      ))}
    </Panel>
  );
}

function ComplexityHealthTab({ cfg, set }) {
  const setOpt = (i, k, v) => set("complexity", { ...cfg.complexity, options: cfg.complexity.options.map((o, j) => (j === i ? { ...o, [k]: v } : o)) });
  const setH = (k, v) => set("health", { ...cfg.health, [k]: v });
  return (
    <>
      <Panel title="Property complexity factors — applied to operational departments only" right={
        <Button variant="outline" size="sm" onClick={() => set("complexity", { ...cfg.complexity, options: [...cfg.complexity.options, { id: `cx_${Date.now()}`, label: "New condition", desc: "", factor: 1 }] })} data-testid="sc-cms-cx-add">
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      }>
        <p className="text-[11px] text-zinc-400 mb-3">Not applied to leadership, security, supervisors, stores or laundry.</p>
        {cfg.complexity.options.map((o, i) => (
          <div key={o.id || i} className="border rounded-lg p-3 mb-2" data-testid={`sc-cms-cx-${i}`}>
            <div className="grid grid-cols-[1fr,1.4fr,100px,36px] gap-3 items-end">
              <Field label="Label" value={o.label} onChange={(v) => setOpt(i, "label", v)} testId={`sc-cms-cx-label-${i}`} />
              <Field label="Description" value={o.desc} onChange={(v) => setOpt(i, "desc", v)} />
              <Field label="Factor" type="number" value={o.factor} onChange={(v) => setOpt(i, "factor", v)} testId={`sc-cms-cx-factor-${i}`} />
              <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => set("complexity", { ...cfg.complexity, options: cfg.complexity.options.filter((_, j) => j !== i) })}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
            <div className="mt-3 border-t pt-2.5">
              <div className="text-[11px] text-zinc-400 mb-1.5">
                Roles <strong>not required</strong> for this property type — click to exclude from the formula ({(o.excluded_roles || []).length} excluded):
              </div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(cfg.roles).map(([rid, r]) => {
                  const off = (o.excluded_roles || []).includes(rid);
                  return (
                    <button key={rid} type="button"
                      onClick={() => setOpt(i, "excluded_roles", off ? (o.excluded_roles || []).filter((x) => x !== rid) : [...(o.excluded_roles || []), rid])}
                      className={`px-2 py-1 rounded-full border text-[11px] transition-colors ${off ? "bg-red-50 border-red-200 text-red-700 line-through" : "bg-white border-zinc-200 text-zinc-600 hover:border-zinc-400"}`}
                      data-testid={`sc-cms-cx-excl-${i}-${rid}`}>{r.label}</button>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </Panel>
      <Panel title="Payroll health check — staffing cost as % of projected revenue">
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Lean below (%)" type="number" value={cfg.health.lean_below} onChange={(v) => setH("lean_below", v)} testId="sc-cms-h-lean" />
          <Field label="Healthy up to (%)" type="number" value={cfg.health.healthy_to} onChange={(v) => setH("healthy_to", v)} testId="sc-cms-h-healthy" />
          <Field label="Review up to (%)" type="number" value={cfg.health.review_to} onChange={(v) => setH("review_to", v)} testId="sc-cms-h-review" />
          <Field label="Lean label" value={cfg.health.lean_label} onChange={(v) => setH("lean_label", v)} />
          <Field label="Healthy label" value={cfg.health.healthy_label} onChange={(v) => setH("healthy_label", v)} />
          <Field label="Review label" value={cfg.health.review_label} onChange={(v) => setH("review_label", v)} />
          <Field label="Unsustainable label" value={cfg.health.unsustainable_label} onChange={(v) => setH("unsustainable_label", v)} className="sm:col-span-2" />
        </div>
      </Panel>
    </>
  );
}

function ContentTab({ cfg, set }) {
  const sub = (key) => (k, v) => set(key, { ...cfg[key], [k]: v });
  const h = sub("hero"), l = sub("labels"), cl = sub("closing");
  return (
    <>
      <Panel title="Hero">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Overline" value={cfg.hero.overline} onChange={(v) => h("overline", v)} testId="sc-cms-hero-overline" />
          <div />
          <Field label="Headline — before accent" value={cfg.hero.headline_pre} onChange={(v) => h("headline_pre", v)} />
          <Field label="Accent word" value={cfg.hero.headline_accent} onChange={(v) => h("headline_accent", v)} />
          <Field label="Headline — after accent" value={cfg.hero.headline_post} onChange={(v) => h("headline_post", v)} />
          <Field label="Subheadline" textarea value={cfg.hero.subheadline} onChange={(v) => h("subheadline", v)} className="sm:col-span-2" />
        </div>
      </Panel>
      <Panel title="Section copy & labels">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Inputs — title" value={cfg.labels.inputs_title} onChange={(v) => l("inputs_title", v)} />
          <Field label="Inputs — intro" value={cfg.labels.inputs_intro} onChange={(v) => l("inputs_intro", v)} />
          <Field label="Permanent team — title" value={cfg.labels.perm_title} onChange={(v) => l("perm_title", v)} />
          <Field label="Permanent team — intro" value={cfg.labels.perm_intro} onChange={(v) => l("perm_intro", v)} />
          <Field label="Event staffing — title" value={cfg.labels.event_title} onChange={(v) => l("event_title", v)} />
          <Field label="Event staffing — intro" value={cfg.labels.event_intro} onChange={(v) => l("event_intro", v)} />
          <Field label="Financial impact — title" value={cfg.labels.finance_title} onChange={(v) => l("finance_title", v)} />
          <Field label="Summary overline" value={cfg.labels.summary_overline} onChange={(v) => l("summary_overline", v)} />
          <Field label="Download button label" value={cfg.labels.download_label} onChange={(v) => l("download_label", v)} />
          <Field label="Discuss button label" value={cfg.labels.discuss_label} onChange={(v) => l("discuss_label", v)} />
          <Field label="Post-assessment adjustment note" textarea value={cfg.labels.adjust_note} onChange={(v) => l("adjust_note", v)} className="sm:col-span-2" />
          <Field label="Disclaimer" textarea value={cfg.labels.disclaimer} onChange={(v) => l("disclaimer", v)} className="sm:col-span-2" />
        </div>
      </Panel>
      <Panel title="Closing strip" right={<label className="flex items-center gap-2 text-xs text-zinc-500">Enabled <Switch checked={cfg.closing.enabled !== false} onCheckedChange={(v) => cl("enabled", v)} /></label>}>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Heading" value={cfg.closing.heading} onChange={(v) => cl("heading", v)} />
          <Field label="Text" value={cfg.closing.text} onChange={(v) => cl("text", v)} />
          <Field label="CTA label" value={cfg.closing.cta_label} onChange={(v) => cl("cta_label", v)} />
        </div>
      </Panel>
    </>
  );
}

function IF({ inp, set, k, label }) {
  return <Field label={label} type="number" value={inp[k]} onChange={(v) => set(k, v)} testId={`sc-cms-int-${k}`} />;
}

function InternalTab({ cfg }) {
  const [inp, setInp] = useState({ rooms: 20, seats: 80, outdoor: 18000, gates: 1, actualCapacity: 1000, complexityId: "standard", eventGuests: "", eventsPerYear: 12, daysPerEvent: 1, revenue: 30000000 });
  const set = (k, v) => setInp((p) => ({ ...p, [k]: v === "" ? "" : Number(v) }));
  const res = useMemo(() => computeStaffing(inp, cfg), [inp, cfg]);
  return (
    <>
      <Panel title="Internal calculator — full formula view (never shown to owners)">
        <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-2">
          <IF inp={inp} set={set} k="rooms" label="Rooms" /><IF inp={inp} set={set} k="seats" label="Restaurant seats" /><IF inp={inp} set={set} k="outdoor" label="Outdoor sqft" /><IF inp={inp} set={set} k="gates" label="Active gates" /><IF inp={inp} set={set} k="actualCapacity" label="Actual capacity (0 = estimate)" />
          <Sel label="Complexity" value={inp.complexityId} onChange={(v) => setInp((p) => ({ ...p, complexityId: v }))} options={cfg.complexity.options.map((o) => [o.id, `${o.label} ×${o.factor}`])} testId="sc-cms-int-complexity" />
          <IF inp={inp} set={set} k="eventsPerYear" label="Events / year" /><IF inp={inp} set={set} k="daysPerEvent" label="Days / event" /><IF inp={inp} set={set} k="revenue" label="Projected revenue (₹)" />
        </div>
      </Panel>
      <Panel title={`Permanent team — ${num(res.totalHeadcount)} people · ${inr(res.monthlyPayroll)}/mo · event capacity ${num(res.E)}`}>
        <table className="w-full text-xs">
          <thead><tr className="text-left text-zinc-400 uppercase tracking-wide text-[10px]">
            <th className="pb-2 pr-4">Department</th><th className="pb-2 pr-4">Formula</th><th className="pb-2 pl-4 text-right whitespace-nowrap">People</th><th className="pb-2 pl-4 text-right whitespace-nowrap min-w-[90px]">Monthly</th><th className="pb-2 pl-4 text-right whitespace-nowrap min-w-[90px]">Annual</th>
          </tr></thead>
          <tbody>
            {res.departments.map((d) => (
              <tr key={d.id} className="border-t border-zinc-100 align-top" data-testid={`sc-cms-int-dept-${d.id}`}>
                <td className="py-2 pr-4 font-medium whitespace-nowrap">{d.label}{d.operational && res.factor !== 1 ? <span className="text-amber-600"> ×{res.factor}</span> : ""}
                  <div className="font-normal text-zinc-400">{d.roles.map((r) => `${r.label} × ${r.count}`).join(" · ")}</div></td>
                <td className="py-2 pr-4 text-zinc-500">{d.formula}</td>
                <td className="py-2 pl-4 text-right tabular-nums whitespace-nowrap">{num(d.headcount)}</td>
                <td className="py-2 pl-4 text-right tabular-nums whitespace-nowrap">{inr(d.monthly)}</td>
                <td className="py-2 pl-4 text-right tabular-nums whitespace-nowrap">{inr(d.annual)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
      <Panel title={`Event staffing — ${num(res.Eg)} guests → ${num(res.event.required)} required − ${num(res.event.deployable)} deployable = ${num(res.event.additionalTemp)} temp · ${inr(res.event.costPerEvent)}/event`}>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1 text-xs">
          {res.event.roles.map((r) => (
            <div key={r.id} className="flex justify-between border-b border-zinc-50 py-1"><span>{r.label}</span><span className="tabular-nums">{num(r.required)} @ {inr(r.rate)}/day</span></div>
          ))}
        </div>
        <div className="mt-3 text-xs text-zinc-500">Deployable base ({num(res.event.deployBase)} operational staff × {cfg.formulas.deployable_pct}%) · Annual temp cost: {inr(res.event.annualTempCost)}</div>
      </Panel>
      <Panel title="Financial impact">
        <div className="grid sm:grid-cols-4 gap-4 text-sm">
          <div><div className="text-[11px] uppercase text-zinc-400">Annual payroll</div><div className="font-semibold mt-1 tabular-nums">{inr(res.annualPayroll)}</div></div>
          <div><div className="text-[11px] uppercase text-zinc-400">Annual temp cost</div><div className="font-semibold mt-1 tabular-nums">{inr(res.event.annualTempCost)}</div></div>
          <div><div className="text-[11px] uppercase text-zinc-400">Total annual staffing</div><div className="font-semibold mt-1 tabular-nums" data-testid="sc-cms-int-total">{inr(res.finance.annualTotal)}</div></div>
          <div><div className="text-[11px] uppercase text-zinc-400">Of revenue</div><div className="font-semibold mt-1" data-testid="sc-cms-int-pct">{res.finance.pct !== null ? `${res.finance.pct.toFixed(1)}% — ${res.finance.status?.label}` : "—"}</div></div>
        </div>
      </Panel>
    </>
  );
}

export default function StaffingCMS() {
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("formulas");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.get("/admin/content/staffing_calculator").then(({ data }) => setC(mergeST(data))).catch(() => setC(mergeST(null)));
  }, []);

  const set = (k, v) => { setDirty(true); setC((prev) => ({ ...prev, [k]: v })); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content/staffing_calculator", c);
      setDirty(false);
      toast.success("Staffing calculator settings updated — live on the public page");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const reset = () => {
    if (!window.confirm("Reset ALL staffing formulas, salaries and content to defaults? You still need to Save.")) return;
    setC(mergeST(null));
    setDirty(true);
  };

  if (!c) return <div className="p-8 text-zinc-400">Loading…</div>;

  const TABS = [["formulas", "Formulas & ratios"], ["salaries", "Salaries"], ["event", "Event staffing"], ["complexity", "Complexity & health"], ["content", "Page content"], ["internal", "Internal calculator"]];

  return (
    <div className="p-6 md:p-8 max-w-6xl" data-testid="staffing-cms">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><HardHat className="h-6 w-6" /> Staffing Calculator Settings</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Every ratio, salary and threshold of the Staffing Requirement Formula.{" "}
            <Link to="/staffing-calculator" target="_blank" className="inline-flex items-center gap-1 text-zinc-800 underline underline-offset-2">View public page <ExternalLink className="w-3 h-3" /></Link>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={reset} data-testid="sc-cms-reset"><RotateCcw className="w-4 h-4 mr-1.5" /> Reset to defaults</Button>
          <Button onClick={save} disabled={saving} data-testid="sc-cms-save">{saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Save</Button>
        </div>
      </div>

      {dirty && <div className="mb-4 text-xs bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-md" data-testid="sc-cms-unsaved">Unsaved changes — click Save to publish.</div>}

      <div className="flex gap-1 mb-5 border-b overflow-x-auto">
        {TABS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-sm border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === k ? "border-zinc-900 font-medium" : "border-transparent text-zinc-400 hover:text-zinc-700"}`}
            data-testid={`sc-cms-tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab === "formulas" && <FormulasTab cfg={c} set={set} />}
      {tab === "salaries" && <SalariesTab cfg={c} set={set} />}
      {tab === "event" && <EventTab cfg={c} set={set} />}
      {tab === "complexity" && <ComplexityHealthTab cfg={c} set={set} />}
      {tab === "content" && <ContentTab cfg={c} set={set} />}
      {tab === "internal" && <InternalTab cfg={c} />}
    </div>
  );
}
